import { v4 } from 'uuid';
import * as Yup from 'yup';
import { getUniqueIds } from '@/utils/selection';

export const NO_BADGE_ASSIGNEE_ID = '00000000-0000-0000-0000-000000000000';

export type BadgeAssigneeEditFormAttribute = {
  id: string;
  title: string;
  options: string[] | null;
  is_required: boolean;
  attribute_type: 'numeric' | 'text';
  default_value: string | null;
  order: number | null;
  badge_attribute_assignees: {
    id: string;
    value: string | null;
  }[];
};

export type BadgeAssigneeEditFormValues = {
  badgeId: string;
  badge_attributes: BadgeAssigneeEditFormAttribute[];
};

type BadgeAssigneeFormAttributeInput = {
  id: string;
  title: string;
  options?: readonly string[] | null;
  is_required: boolean;
  attribute_type: 'numeric' | 'text';
  default_value: string | null;
  order: number | null;
  badge_attribute_assignees?: readonly {
    id?: string | null;
    value?: string | null;
  }[];
};

export function getBadgeAssigneeEditFormValues({
  assigneeId,
  badgeAttributes,
  badgeId,
}: {
  assigneeId?: string;
  badgeAttributes: readonly BadgeAssigneeFormAttributeInput[];
  badgeId?: string | null;
}): BadgeAssigneeEditFormValues {
  return {
    badgeId: badgeId ?? '',
    badge_attributes: badgeAttributes.map((badgeAttribute) => {
      const existingAssigneeAttribute =
        badgeAttribute.badge_attribute_assignees?.[0];

      return {
        id: badgeAttribute.id,
        title: badgeAttribute.title,
        options: badgeAttribute.options ? [...badgeAttribute.options] : null,
        is_required: badgeAttribute.is_required,
        attribute_type: badgeAttribute.attribute_type,
        default_value: badgeAttribute.default_value,
        order: badgeAttribute.order,
        badge_attribute_assignees: [
          {
            id: existingAssigneeAttribute?.id ?? v4(),
            value: !assigneeId
              ? badgeAttribute.default_value
              : (existingAssigneeAttribute?.value ?? ''),
          },
        ],
      };
    }),
  };
}

export const badgeAssigneeEditValidator: Yup.ObjectSchema<BadgeAssigneeEditFormValues> =
  Yup.object({
    badgeId: Yup.string().required(),
    badge_attributes: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required(),
          title: Yup.string().required(),
          options: Yup.array().of(Yup.string().required()).nullable().defined(),
          is_required: Yup.boolean().required(),
          attribute_type: Yup.mixed<'numeric' | 'text'>()
            .oneOf(['numeric', 'text'])
            .required(),
          default_value: Yup.string().nullable().defined(),
          order: Yup.number().nullable().defined(),
          badge_attribute_assignees: Yup.array()
            .of(
              Yup.object({
                id: Yup.string().defined(),
                value: Yup.string().nullable().defined(),
              }).required(),
            )
            .required(),
        }).required(),
      )
      .required()
      .test('validate-attributes', function (badgeAttributes) {
        if (!badgeAttributes) return true;

        for (const [index, attr] of badgeAttributes.entries()) {
          const value = attr?.badge_attribute_assignees?.[0]?.value ?? '';

          if (attr?.is_required && (!value || value.trim() === '')) {
            return this.createError({
              path: `badge_attributes[${index}].badge_attribute_assignees[0].value`,
              message: 'Required',
            });
          }

          if (
            value &&
            value.trim() !== '' &&
            attr?.attribute_type === 'numeric'
          ) {
            const numValue = Number(value);
            if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
              return this.createError({
                path: `badge_attributes[${index}].badge_attribute_assignees[0].value`,
                message: `Non-Number not allowed: ${value}`,
              });
            }
          }
        }

        return true;
      }),
  });

export function getTargetPersonIds({
  personId,
  selected,
}: {
  personId?: string;
  selected?: string;
}) {
  const selectedPersonIds =
    selected
      ?.split(',')
      .map((selectedPersonId) => selectedPersonId.trim())
      .filter((selectedPersonId) => selectedPersonId.length > 0) ?? [];
  const personIds = personId
    ? [personId, ...selectedPersonIds]
    : selectedPersonIds;

  return getUniqueIds(personIds);
}
